import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button, Paper, Tab, Tabs } from '@material-ui/core';
import i18n from 'i18n-js';
import { parsePhoneNumber } from 'react-phone-number-input';

import { default as restaurantStore } from 'store/restaurantDetailsStore';
import categoryStore from 'store/categoryStore';
import financeStore from 'store/financeStore';
import {
    getCoordinatesFromTheAddress,
    replaceColonWithDotServicesHours,
    trimLeftAndRight,
} from 'helpers/helperFunctions';
import QR from './qr';
import { ContactPersons, Categories, RestaurantDetails } from './components';
import { images } from 'assets';
import styles from './styles';
import PaymentDetails from './paymentDetails';
import globalStore from 'store/globalStore';
import { ImageContainerRefProps } from 'components/shared/components/ImageContainer';
import { OrderServices } from 'constants/enums';
import { actionCreators } from 'store/restaurantDetailsStore/actionCreators';
import { useAppSelector } from 'index';
import agent from 'api/agent';
import AdvancedPricing from './advancedPricing';
import AdvancedSettings from './advancedSettings';
import { theme } from 'config/theme';
import { LocalStorageKeys } from 'constants/localStorageKeys';

enum TabViews {
    RestaurantDetails = 1,
    WorkAereas = 2,
    ContactPersons = 3,
    PaymentDetails = 4,
    AdvancedPricing = 5,
    QR = 6,
    AdvancedSettings = 7,
}

export default function RestaurantInformation() {
    const classes = styles();

    const dispatch = useDispatch();

    const categoryDetails = useAppSelector((state) => state.category.categoryDetails);
    const restaurantInformation = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation
    );
    const user = useAppSelector((state) => state.user);
    const lang = useAppSelector((state) => state.global.language);

    const [view, setView] = useState(TabViews.RestaurantDetails);
    const [formChanged, setFormChanged] = useState(false);
    const [orderBy, setOrderBy] = useState('name');
    const [asc, setAsc] = useState(true);
    const [downloadDineInQrName, setDownloadDineInQrName] = useState<string>('');
    const [downloadTakeAwayQrName, setDownloadTakeAwayQrName] = useState<string>('');
    const [downloadHomeDeliveryQrName, setDownloadHomeDeliveryQrName] = useState<string>('');

    const formikRef: any = useRef(null);
    const logoUploadRef = useRef<ImageContainerRefProps>(null);
    const photoUploadRef = useRef<ImageContainerRefProps>(null);
    const paymentDetailsRef = useRef(null);

    const orderNames: string[] = ['name', 'count'];

    function handleDownloadQRName(name: string, service: OrderServices) {
        switch (service) {
            case OrderServices.HomeDelivery:
                setDownloadHomeDeliveryQrName(name);
                break;
            case OrderServices.DineIn:
                setDownloadDineInQrName(name);
                break;
            default:
                setDownloadTakeAwayQrName(name);
        }
    }

    function isSaveQRButtonDisabled() {
        if (restaurantInformation.dineIn.isActive) {
            if (
                !!downloadDineInQrName.trim() &&
                downloadDineInQrName !== restaurantInformation.qrCodesData.dineInQrCodeName &&
                downloadDineInQrName.length < 40
            ) {
                return false;
            }
        }
        if (restaurantInformation.homeDelivery.isActive) {
            if (
                !!downloadHomeDeliveryQrName.trim() &&
                downloadHomeDeliveryQrName !==
                    restaurantInformation.qrCodesData.homeDeliveryQrName &&
                downloadHomeDeliveryQrName.length < 40
            ) {
                return false;
            }
        }
        if (restaurantInformation.takeAway.isActive) {
            if (
                !!downloadTakeAwayQrName.trim() &&
                downloadTakeAwayQrName !== restaurantInformation.qrCodesData.takeAwayQrCodeName &&
                downloadTakeAwayQrName.length < 40
            ) {
                return false;
            }
        }

        return true;
    }

    useEffect(() => {
        !!!restaurantInformation.id &&
            dispatch(restaurantStore.actionCreators.getRestaurantInformation());
        !!!categoryDetails &&
            dispatch(categoryStore.actionCreators.getCategoryDetails(asc, orderBy));
        dispatch(restaurantStore.actionCreators.getCuisineTypes());

        checkLastTab();

        return () => {
            removeLastTab();
        };
    }, []);

    useEffect(() => {
        setDownloadDineInQrName(restaurantInformation.qrCodesData.dineInQrCodeName);
        setDownloadTakeAwayQrName(restaurantInformation.qrCodesData.takeAwayQrCodeName);
        setDownloadHomeDeliveryQrName(restaurantInformation.qrCodesData.homeDeliveryQrName);
    }, [restaurantInformation.qrCodesData]);

    function checkLastTab() {
        const lastTab = localStorage.getItem(LocalStorageKeys.restaurantInfoTab);
        if (lastTab) {
            setView(parseInt(lastTab));
        }
    }

    function saveLastTab(index: number) {
        localStorage.setItem(LocalStorageKeys.restaurantInfoTab, index.toString());
    }

    /**
     * Removes last used tab in case of user leaves this restaurant information page
     */
    function removeLastTab() {
        localStorage.removeItem(LocalStorageKeys.restaurantInfoTab);
    }

    function changeTab(event: React.ChangeEvent<{}>, view: number) {
        saveLastTab(view);
        setView(view);
    }

    function handleOrder(id: string) {
        if (id === orderBy) {
            dispatch(categoryStore.actionCreators.getCategoryDetails(!asc, orderBy));
            setAsc(!asc);
        } else {
            setOrderBy(id);
            dispatch(categoryStore.actionCreators.getCategoryDetails(asc, id));
        }
    }

    function createCategory(categoryName: string, dishes: string[], position: string) {
        dispatch(
            categoryStore.actionCreators.createCategory({
                Name: categoryName,
                Dishes: dishes,
                position,
            })
        );
    }

    function editCategory(
        categoryName: string,
        dishes: string[],
        position: string,
        categoryId: string
    ) {
        dispatch(
            categoryStore.actionCreators.editCategory(
                {
                    Name: categoryName,
                    Dishes: dishes,
                    position,
                },
                categoryId
            )
        );
    }

    function disableOrEnableCategory(categoriesIds: string[]) {
        dispatch(categoryStore.actionCreators.disableOrEnableCategory(categoriesIds));
    }

    function deleteCategory(categoryId: string) {
        dispatch(categoryStore.actionCreators.deleteCategory(categoryId));
    }

    function renderTabs() {
        switch (view) {
            case TabViews.RestaurantDetails:
                return (
                    <>
                        <RestaurantDetails
                            onFormChange={() => setFormChanged(true)}
                            restaurantInformation={restaurantInformation}
                            formikRef={formikRef}
                            logoUploadRef={logoUploadRef}
                            photoUploadRef={photoUploadRef}
                        />
                    </>
                );
            case TabViews.WorkAereas:
                if (categoryDetails)
                    return (
                        <>
                            <Categories
                                categoryDetails={categoryDetails}
                                onCreate={createCategory}
                                onEdit={editCategory}
                                onDisableOrEnable={disableOrEnableCategory}
                                onDelete={deleteCategory}
                                onOrder={handleOrder}
                                orderBy={orderBy}
                                orderNames={orderNames}
                                asc={asc}
                                onSaveColor={(id, color) => {
                                    dispatch(
                                        categoryStore.actionCreators.setCategoryColor(id, color)
                                    );
                                }}
                            />
                        </>
                    );
                return;
            case TabViews.ContactPersons:
                return <ContactPersons />;
            case TabViews.PaymentDetails:
                return (
                    <PaymentDetails
                        paymentDetailsRef={paymentDetailsRef}
                        onFormChange={setFormChanged}
                    />
                );
            case TabViews.AdvancedPricing:
                return <AdvancedPricing />;
            case TabViews.QR:
                return (
                    <QR
                        restaurantId={restaurantInformation.id}
                        downloadDineInQrName={downloadDineInQrName}
                        downloadTakeAwayQrName={downloadTakeAwayQrName}
                        downloadHomeDeliveryQrName={downloadHomeDeliveryQrName}
                        setDownloadQrName={handleDownloadQRName}
                    />
                );
            case TabViews.AdvancedSettings:
                return <AdvancedSettings />;
        }
    }

    function resetForm() {
        dispatch({
            type: 'RESTAURANT_DETAILS_GET_START',
        });

        formikRef.current.resetForm();
        setFormChanged(false);

        setTimeout(() => {
            dispatch({
                type: 'SET_RESTAURANT_IS_LOADING_FALSE',
            });
        }, 250);
    }

    async function updateRestaurantInformation() {
        const errors = formikRef.current.errors;
        const errorsLength = Object.keys(errors).length;

        if (errorsLength === 0) {
            let { logoUrl, photoUrl } = restaurantInformation;
            const formValues = { ...formikRef.current.values };
            const typeIds: string[] = [];

            Object.keys(formValues.type).forEach((x) => {
                typeIds.push(formValues.type[x].id);
            });

            if (!typeIds.length) {
                return formikRef.current.setFieldError('key', 'val');
            }

            formValues.type = typeIds;

            dispatch({
                type: 'UPDATE_RESTAURANT_INFORMATION_START',
            });

            await agent.User.SetUserCurrentRestaurant(restaurantInformation.id);

            if (
                formValues.latitude === 0 ||
                formValues.longitude === 0 ||
                formValues.addressLine !== restaurantInformation.addressLine ||
                restaurantInformation.city !== formValues.city ||
                restaurantInformation.zip !== formValues.zip ||
                !restaurantInformation.timeZone
            ) {
                const { latitude, longitude, timeZone } = await getCoordinatesFromTheAddress(
                    `${formValues.addressLine}, ${formValues.zip} ${formValues.city}`
                );

                if (latitude === 0 || longitude === 0) {
                    dispatch(
                        globalStore.actionCreators.showToaster(
                            'warning',
                            i18n.t('warnings.writeMoreDetailedAddress')
                        )
                    );
                    dispatch({
                        type: 'UPDATE_RESTAURANT_INFORMATION_ERROR',
                    });
                    return;
                }

                formValues.latitude = latitude;
                formValues.longitude = longitude;
                formValues.timeZone = timeZone;
            }

            if (formValues.logoUrl !== logoUrl) {
                const logoUrl = await logoUploadRef.current?.uploadImage();
                formValues.logoUrl = logoUrl;
            }
            if (formValues.photoUrl !== photoUrl) {
                const photoUrl = await photoUploadRef.current?.uploadImage();
                formValues.photoUrl = photoUrl;
            }

            /**
             * Required to avoid backend validation errors
             * time format should be with dots - 10.30
             */
            replaceColonWithDotServicesHours(formValues);

            const parsedPhoneNumber = parsePhoneNumber(formValues.phoneNumber);
            if (parsedPhoneNumber) {
                if (!parsedPhoneNumber.country) {
                    formValues.phoneNumber = formValues.phoneNumber.replace(
                        '+' + parsedPhoneNumber.countryCallingCode.toString(),
                        user.browserData?.country_calling_code
                    );
                }
            }

            dispatch(
                restaurantStore.actionCreators.updateRestaurantInformation({
                    ...formValues,
                    name: trimLeftAndRight(formValues.name),
                    addressLine: trimLeftAndRight(formValues.addressLine),
                    city: trimLeftAndRight(formValues.city),
                    website: trimLeftAndRight(formValues.website),
                    facebook: trimLeftAndRight(formValues.facebook),
                    instagram: trimLeftAndRight(formValues.instagram),
                    description: trimLeftAndRight(formValues.description),
                    dineIn: formValues.dineIn,
                    homeDelivery: formValues.homeDelivery,
                    takeAway: formValues.takeAway,
                })
            );
            setFormChanged(false);
        } else {
            Object.keys(errors).forEach(function (key, index) {
                const val = errors[key];
                formikRef.current.setFieldError(key, val);
            });
        }
    }

    function getTabs() {
        return [
            <Tab
                key={TabViews.RestaurantDetails}
                value={TabViews.RestaurantDetails}
                label={i18n.t('restaurant.restaurantDetails')}
            />,
            <Tab
                key={TabViews.WorkAereas}
                value={TabViews.WorkAereas}
                label={i18n.t('restaurant.workAreas')}
            />,
            <Tab
                key={TabViews.ContactPersons}
                value={TabViews.ContactPersons}
                label={i18n.t('restaurant.contactPersons')}
            />,
            <Tab
                key={TabViews.PaymentDetails}
                value={TabViews.PaymentDetails}
                label={i18n.t('restaurant.paymentDetails')}
            />,
            <Tab
                key={TabViews.AdvancedPricing}
                value={TabViews.AdvancedPricing}
                label={i18n.t('restaurant.advancedPricing')}
            />,
            <Tab key={TabViews.QR} value={TabViews.QR} label={i18n.t('restaurant.qr')} />,
            <Tab
                key={TabViews.AdvancedSettings}
                value={TabViews.AdvancedSettings}
                label={i18n.t('common.advancedSettings')}
            />,
        ];
    }

    function getButtons() {
        switch (view) {
            case TabViews.RestaurantDetails:
                return (
                    <div className={classes.tabButtons}>
                        <Button variant="outlined" color="primary" onClick={() => resetForm()}>
                            {i18n.t('common.cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={!formChanged}
                            onClick={() => updateRestaurantInformation()}
                        >
                            {i18n.t('common.save')}
                        </Button>
                    </div>
                );
            case TabViews.PaymentDetails:
                return (
                    <div style={{ width: lang === 'en' ? 90 : 120 }}>
                        <Button
                            className={classes.saveChangesButton}
                            variant="contained"
                            color="primary"
                            disabled={!formChanged}
                            onClick={() => {
                                // @ts-ignore
                                paymentDetailsRef.current.submitForm();
                            }}
                        >
                            {i18n.t('common.save')}
                        </Button>
                    </div>
                );
            case TabViews.QR:
                return (
                    <div style={{ width: lang === 'en' ? 90 : 120 }}>
                        <Button
                            className={classes.saveChangesButton}
                            variant="contained"
                            disabled={isSaveQRButtonDisabled()}
                            color="primary"
                            onClick={() => {
                                dispatch(
                                    actionCreators.updateQrCodesNames({
                                        dineInQrCodeName: downloadDineInQrName,
                                        takeAwayQrCodeName: downloadTakeAwayQrName,
                                        homeDeliveryQrName: downloadHomeDeliveryQrName,
                                    })
                                );
                            }}
                        >
                            {i18n.t('common.save')}
                        </Button>
                    </div>
                );
            case TabViews.AdvancedSettings:
                return <div style={{ width: lang === 'en' ? 90 : 120 }}></div>;

            default:
                return null;
        }
    }

    return (
        <>
            <Paper className={classes.mainContainer}>
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${theme.palette.grey['300']}`,
                    }}
                >
                    <Tabs
                        id="hide-in-print"
                        className={classes.tab}
                        value={view}
                        onChange={changeTab}
                        variant="scrollable"
                        aria-label="wrapped label tabs example"
                        indicatorColor="primary"
                        textColor="primary"
                    >
                        {getTabs()}
                    </Tabs>
                    {getButtons()}
                </div>

                <div className={classes.sectionContainer}>{renderTabs()}</div>
            </Paper>
        </>
    );
}
